import { api } from "./client";
import type { ApiResponse } from "./types";

export interface EventOrganizer {
  id: string;
  userId: string;
  displayName: string;
  bio: string | null;
  avatarUrl: string | null;
  isVerified: boolean;
  verifiedAt: string | null;
  eventCount: number;
  totalAttendees: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  verificationRequest?: {
    id: string;
    status:
      | "pending"
      | "under_review"
      | "approved"
      | "rejected"
      | "more_info_requested";
    submittedAt: string;
    reviewedAt: string | null;
    reviewNotes: string | null;
    rejectReason: string | null;
  } | null;
}

export interface RegisterEventOrganizerInput {
  displayName: string;
  bio?: string;
  avatarUrl?: string;
}

export interface CoOrganizerInvite {
  id: string;
  userId: string;
  role: string;
  title: string | null;
  user: { id: string; name: string; email: string; avatarUrl: string | null };
}

export function registerEventOrganizer(input: RegisterEventOrganizerInput) {
  return api.post<ApiResponse<EventOrganizer>>("/event-organizers/register", input);
}

export function getMyEventOrganizer() {
  return api.get<ApiResponse<EventOrganizer>>("/event-organizers/me");
}

export function updateMyEventOrganizer(input: Partial<RegisterEventOrganizerInput>) {
  return api.patch<ApiResponse<EventOrganizer>>("/event-organizers/me", input);
}

export function getEventOrganizer(id: string) {
  return api.get<ApiResponse<EventOrganizer>>(`/event-organizers/${id}`);
}

export function inviteCoOrganizer(
  eventOrganizerId: string,
  input: { email: string; title?: string },
) {
  return api.post<ApiResponse<CoOrganizerInvite>>(
    `/event-organizers/${eventOrganizerId}/co-organizers/invite`,
    input,
  );
}

// ─── Directory (E6.3) — find a seeded organizer to claim ────────────

export type ProspectOrgType =
  | "regional_linguistic"
  | "pan_indian_cultural"
  | "cultural"
  | "educational"
  | "community_center"
  | "student_alumni";

export interface OrganizerDirectoryEntry {
  id: string;
  slug: string;
  orgName: string;
  acronymAliases: string[];
  websiteUrl: string | null;
  city: string | null;
  state: string | null;
  metroArea: string | null;
  orgType: ProspectOrgType | null;
  regionOrLanguage: string | null;
  foundedYear: number | null;
  primarySocialUrl: string | null;
  keyPerson: string | null;
  isClaimed: boolean;
  eventOrganizerId: string | null;
}

export interface DirectoryListParams {
  search?: string;
  metro?: string;
  state?: string;
  orgType?: ProspectOrgType | "";
  page?: number;
  limit?: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export function listOrganizerDirectory(params: DirectoryListParams) {
  return api.get<
    ApiResponse<{ items: OrganizerDirectoryEntry[]; meta: PaginationMeta }>
  >("/event-organizers/directory", params as Record<string, string | number | undefined>);
}

export function getOrganizerDirectoryEntry(id: string) {
  return api.get<ApiResponse<OrganizerDirectoryEntry>>(
    `/event-organizers/directory/${id}`,
  );
}
